import React, { useState, useEffect } from 'react';
import { formatMoney } from '../utils/dealExtras';

import { loadStripe } from '@stripe/stripe-js';

import { Elements, CardNumberElement, useStripe, useElements } from '@stripe/react-stripe-js';

import { theme } from '../theme';

import { getSavedPaymentMethod, getStripeConfig } from '../api/atlast';

import CardFieldsGroup from '../CardFieldsGroup';
import { usdParen } from '../utils/fee';



// Rebuilt (Sep 23, per Ali's report — the checkout currency batch):

// previously showed a flat "$9.99 USD" everywhere, including "Freeze

// this price — $9.99 USD" as the headline, when the actual Stripe

// hold is placed in the pin's own currency (converted server-side via

// fx.convert_amount in create_freeze). A Canadian customer reading

// "USD" and then seeing a CAD hold on their statement is exactly the

// mismatch this fixes. Now takes a real `quote` object (from

// FreezeDurationModal's GET /freezes/quote/{pin_id} call, carried

// forward rather than re-fetched, so the numbers on this screen match

// the ones the customer just saw on the last one) and shows the same

// three-row "What you'll pay" table used on the Watch screen and in

// every Price Watch email — one explanation of the rule, not a

// different one on every screen it appears.

//

// `onChangeCard` (Sep 23, new): the saved-card path previously had NO

// way to switch cards — a genuine usability gap Ali's question about

// this screen surfaced. Clicking "Change" now swaps the summary row

// for the real new-card entry fields, in place, without leaving the

// modal; saving there replaces the saved card for next time.

function FreezePaymentModal({ userId, pin, quote, resolvesAt, todaysPrice, onConfirm, onCancel }) {

  const [loading, setLoading] = useState(true);

  const [stripeConfigured, setStripeConfigured] = useState(false);

  const [stripePromise, setStripePromise] = useState(null);

  const [savedMethod, setSavedMethod] = useState(null);

  const [useNewCard, setUseNewCard] = useState(false);



  useEffect(() => {

    let cancelled = false;

    (async () => {

      try {

        const config = await getStripeConfig();

        if (cancelled) return;

        if (config.publishable_key) {

          setStripeConfigured(true);

          setStripePromise(loadStripe(config.publishable_key));

          const saved = await getSavedPaymentMethod(userId).catch(() => null);

          if (!cancelled && saved?.has_saved_card) setSavedMethod(saved);

        }

      } catch (e) {

        console.error('FreezePaymentModal: failed to load Stripe config', e);

      } finally {

        if (!cancelled) setLoading(false);

      }

    })();

    return () => { cancelled = true; };

  }, [userId]);



  const showSavedCardPath = !loading && (!stripeConfigured || (savedMethod && !useNewCard));

  const showNewCardPath = !loading && stripeConfigured && (!savedMethod || useNewCard);



  return (

    <div

      onClick={onCancel}

      style={{

        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',

        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'

      }}

    >

      <div

        onClick={(e) => e.stopPropagation()}

        style={{

          background: theme.surface, border: `2px solid ${theme.border}`, borderRadius: '0px',

          maxWidth: '420px', width: '100%', maxHeight: '90vh', overflowY: 'auto'

        }}

      >

        {loading ? (

          <div style={{ padding: '40px', textAlign: 'center' }}>

            <p style={{ fontSize: '13px', color: theme.textMuted }}>Loading…</p>

          </div>

        ) : showSavedCardPath ? (

          <ConfirmWithoutNewCard

            pin={pin}

            quote={quote}

            resolvesAt={resolvesAt}

            todaysPrice={todaysPrice}

            savedMethod={savedMethod}

            onConfirm={() => onConfirm(null, false)}

            onCancel={onCancel}

            onChangeCard={stripeConfigured && savedMethod ? () => setUseNewCard(true) : null}

          />

        ) : showNewCardPath ? (

          <Elements stripe={stripePromise}>

            <NewCardForm

              pin={pin}

              quote={quote}

              resolvesAt={resolvesAt}

              todaysPrice={todaysPrice}

              onConfirm={onConfirm}

              onCancel={onCancel}

              onBackToSaved={savedMethod ? () => setUseNewCard(false) : null}

            />

          </Elements>

        ) : null}

      </div>

    </div>

  );

}



export default FreezePaymentModal;



// Shared "What you'll pay" table (Sep 23) — used by both the

// saved-card and new-card paths below, so the rule is written once,

// not duplicated and at risk of drifting between the two the way the

// old flat "$X.XX USD" line was independently hardcoded in both.

// Real prices where the outcome changes, not amounts added or

// subtracted (Ali's approved wording, same shape as the Watch screen's

// timeline and every Price Watch email).

function PayTable({ quote, resolvesAt, todaysPrice }) {

  if (!quote) {

    return (

      <p style={{ fontSize: '12px', color: theme.textMuted, marginBottom: '16px' }}>Loading today's price…</p>

    );

  }

  const cur = quote.currency;
  // Absolute price levels, not the raw +/- deltas (Sep 24, per Ali's
  // report — real confusion: "7.07 CAD or more" reads like the price
  // itself needs to be 7.07 CAD, not "7.07 CAD ABOVE today's price."
  // threshold_up_local/threshold_down_local are deltas from
  // todaysPrice, not standalone prices — this converts them to the
  // real numbers a customer would actually see the fare at. Falls
  // back to the raw delta only if todaysPrice genuinely isn't
  // available yet, so the table never shows blank/NaN.
  const upperLevel = todaysPrice != null ? todaysPrice + quote.threshold_up_local : quote.threshold_up_local;
  const lowerLevel = todaysPrice != null ? todaysPrice - quote.threshold_down_local : quote.threshold_down_local;

  const whenText = resolvesAt

    ? new Date(resolvesAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

    : null;

  return (

    <>

      <p style={{ fontSize: '12.5px', fontWeight: 700, color: theme.textMuted, marginBottom: '6px' }}>

        {whenText ? `If the price on ${whenText} is…` : "What you'll pay"}

      </p>

      <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${theme.border}`, marginBottom: '14px' }}>

        <tbody>

          <tr>

            <td style={{ padding: '8px 10px', fontSize: '12.5px', color: theme.textPrimary, borderBottom: `1px solid ${theme.border}` }}>{formatMoney(upperLevel)} {cur} or more</td>

            <td style={{ padding: '8px 10px', fontSize: '12.5px', fontWeight: 800, color: theme.structural, textAlign: 'right', borderBottom: `1px solid ${theme.border}` }}>Nothing</td>

          </tr>

          <tr>

            <td style={{ padding: '8px 10px', fontSize: '12.5px', color: theme.textPrimary, borderBottom: `1px solid ${theme.border}` }}>{formatMoney(lowerLevel)} {cur} or less</td>

            <td style={{ padding: '8px 10px', fontSize: '12.5px', fontWeight: 800, color: theme.structural, textAlign: 'right', borderBottom: `1px solid ${theme.border}` }}>Nothing</td>

          </tr>

          <tr>

            <td style={{ padding: '8px 10px', fontSize: '12.5px', color: theme.textPrimary }}>Between {formatMoney(lowerLevel)} and {formatMoney(upperLevel)} {cur}</td>

            <td style={{ padding: '8px 10px', fontSize: '12.5px', fontWeight: 800, color: theme.structural, textAlign: 'right' }}>{formatMoney(quote.fee_local)} {cur}</td>

          </tr>

        </tbody>

      </table>

      <p style={{ fontSize: '12px', color: theme.textMuted, lineHeight: 1.5, marginBottom: '14px' }}>

        Only the price at the end counts. Nothing is charged today — we hold <b>{formatMoney(quote.fee_local)} {cur}{usdParen(cur, formatMoney(quote.fee_usd))}</b> on your card and remove the hold if you don't owe anything.

      </p>

      <p style={{ fontSize: '12px', color: theme.textMuted, lineHeight: 1.5, marginBottom: '14px' }}>

        <b>Booked and paid more than your starting price?</b> If you were charged the fee, send your booking confirmation within 48 hours of your result. If it shows you paid more than your starting price, we'll refund the fee after a quick review.

      </p>

      <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, padding: '10px 12px', marginBottom: '16px' }}>

        <p style={{ fontSize: '12px', color: theme.textMuted, lineHeight: 1.5, margin: 0 }}>

          <b>What it isn't:</b> we don't reserve the fare. If it rises, you'd pay the new price when you book.

        </p>

      </div>

    </>

  );

}



function TripLine({ pin, todaysPrice, quote }) {

  if (!pin) return null;

  const route = pin.origin_code && pin.destination_code ? `${pin.origin_code} → ${pin.destination_code}` : pin.destination;

  const dates = pin.departure_date ? `${pin.departure_date}${pin.return_date ? `–${pin.return_date}` : ''}` : null;

  return (

    <p style={{ fontSize: '12.5px', color: theme.textMuted, marginBottom: '16px' }}>

      {[route, dates, todaysPrice != null && quote ? `today's price ${formatMoney(Number(todaysPrice))} ${quote.currency}` : null].filter(Boolean).join(' · ')}

    </p>

  );

}



// Path 1: Stripe not configured yet, or a saved+consented card already

// exists and the customer hasn't asked to change it — no new card

// entry needed, just a real confirmation of what's about to happen

// before money moves (or a mocked watch, pre-Stripe).

function ConfirmWithoutNewCard({ pin, quote, resolvesAt, todaysPrice, savedMethod, onConfirm, onCancel, onChangeCard }) {

  const [submitting, setSubmitting] = useState(false);



  const handleClick = () => {

    setSubmitting(true);

    onConfirm();

  };



  return (

    <div style={{ padding: '28px 24px' }}>

      <p style={{ fontFamily: theme.fontDisplay, fontSize: '17px', fontWeight: 800, color: theme.textPrimary, marginBottom: '6px' }}>

        Start your Price Watch

      </p>

      <TripLine pin={pin} todaysPrice={todaysPrice} quote={quote} />



      <PayTable quote={quote} resolvesAt={resolvesAt} todaysPrice={todaysPrice} />



      <div style={{ background: theme.bg, border: `2px solid ${theme.border}`, padding: '14px', marginBottom: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

        <span style={{ fontSize: '13px', color: theme.textPrimary, fontWeight: 600 }}>

          {savedMethod

            ? `${savedMethod.brand ? savedMethod.brand.charAt(0).toUpperCase() + savedMethod.brand.slice(1) : 'Card'} •••• ${savedMethod.last4}`

            : 'No charge — Stripe not yet live'}

        </span>

        {/* Real "Change" affordance (Sep 23) — previously the saved

            card was shown with no way to switch it from here at all;

            the only route was leaving this modal, removing the saved

            card on the Payments screen, then starting over. */}

        {onChangeCard && (

          <span onClick={onChangeCard} style={{ fontSize: '12.5px', fontWeight: 700, color: theme.structural, textDecoration: 'underline', cursor: 'pointer' }}>

            Change

          </span>

        )}

      </div>



      <div style={{ display: 'flex', gap: '10px' }}>

        <button

          onClick={handleClick}

          disabled={submitting || !quote}

          style={{

            flex: 1, background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '0px',

            padding: '13px', fontWeight: 800, fontSize: '14px', fontFamily: theme.fontDisplay,

            cursor: (submitting || !quote) ? 'default' : 'pointer', opacity: (submitting || !quote) ? 0.6 : 1

          }}

        >

          {submitting ? 'Starting…' : quote ? `Start watch — ${formatMoney(quote.fee_local)} ${quote.currency}` : 'Start watch'}

        </button>

        <button

          onClick={onCancel}

          disabled={submitting}

          style={{

            flex: 1, background: '#fff', color: theme.textPrimary, border: `2px solid ${theme.border}`, borderRadius: '0px',

            padding: '13px', fontWeight: 700, fontSize: '14px', fontFamily: theme.fontDisplay,

            cursor: submitting ? 'default' : 'pointer'

          }}

        >

          Cancel

        </button>

      </div>

    </div>

  );

}



// Path 2: Stripe configured, and either no saved card exists or the

// customer clicked "Change" on the saved one — real Stripe Elements

// card entry. Same security pattern as PaymentMethodScreen.js's

// AddCardFormFields: the actual card number field renders in Stripe's

// own secure iframe, so raw card data never touches this app's state

// or backend — stripe.createPaymentMethod() sends it straight to

// Stripe, and only the resulting safe paymentMethod.id comes back here

// to be handed to onConfirm.

function NewCardForm({ pin, quote, resolvesAt, todaysPrice, onConfirm, onCancel, onBackToSaved }) {

  const stripe = useStripe();

  const elements = useElements();

  const [saveCard, setSaveCard] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState(null);



  const handleSubmit = async () => {

    if (!stripe || !elements || !quote) return;

    setSubmitting(true);

    setError(null);



    const cardElement = elements.getElement(CardNumberElement);

    const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({

      type: 'card',

      card: cardElement,

    });



    if (pmError) {

      setError(pmError.message || 'Could not process this card. Check the details and try again.');

      setSubmitting(false);

      return;

    }



    onConfirm(paymentMethod.id, saveCard);

  };



  return (

    <div style={{ padding: '28px 24px' }}>

      <p style={{ fontFamily: theme.fontDisplay, fontSize: '17px', fontWeight: 800, color: theme.textPrimary, marginBottom: '6px' }}>

        Start your Price Watch

      </p>

      <TripLine pin={pin} todaysPrice={todaysPrice} quote={quote} />



      <PayTable quote={quote} resolvesAt={resolvesAt} todaysPrice={todaysPrice} />



      <CardFieldsGroup />



      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', margin: '14px 0' }}>

        <span style={{ fontSize: '12px', color: theme.textMuted, lineHeight: 1.5 }}>

          Payment secured by <b style={{ color: '#635BFF' }}>Stripe</b> — <b style={{ color: theme.textPrimary }}>FlyAtlast never sees or stores your card number.</b>

        </span>

      </div>



      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '18px', cursor: 'pointer' }}>

        <input

          type="checkbox"

          checked={saveCard}

          onChange={(e) => setSaveCard(e.target.checked)}

          style={{ marginTop: '2px', width: '16px', height: '16px', flexShrink: 0 }}

        />

        <span style={{ fontSize: '12.5px', color: theme.textMuted, lineHeight: 1.5, display: 'block', width: '100%' }}>

          Save this card for future Price Watch checkouts. I can remove it anytime.

        </span>

      </label>



      {error && (

        <p style={{ fontSize: '12.5px', color: theme.danger, marginBottom: '12px', fontWeight: 600 }}>{error}</p>

      )}



      <div style={{ display: 'flex', gap: '10px' }}>

        <button

          onClick={handleSubmit}

          disabled={!stripe || submitting || !quote}

          style={{

            flex: 1, background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '0px',

            padding: '13px', fontWeight: 800, fontSize: '14px', fontFamily: theme.fontDisplay,

            cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.6 : 1

          }}

        >

          {submitting ? 'Starting…' : quote ? `Start watch — ${formatMoney(quote.fee_local)} ${quote.currency}` : 'Start watch'}

        </button>

        <button

          onClick={onBackToSaved || onCancel}

          disabled={submitting}

          style={{

            flex: 1, background: '#fff', color: theme.textPrimary, border: `2px solid ${theme.border}`, borderRadius: '0px',

            padding: '13px', fontWeight: 700, fontSize: '14px', fontFamily: theme.fontDisplay,

            cursor: submitting ? 'default' : 'pointer'

          }}

        >

          {onBackToSaved ? 'Use saved card' : 'Cancel'}

        </button>

      </div>

    </div>

  );

}